/**
 * Debounced writer between the editor and a `ProjectStore`.
 *
 * The editor calls `schedule` on every change; the controller coalesces a burst
 * of typing into a single write, keeps only the newest version of each record,
 * and serialises writes so two saves of the same project cannot interleave.
 * `flush` exists for `pagehide` / `visibilitychange`, where there is no time
 * left to wait out the debounce.
 */
import type { ProjectStore } from './ProjectStore';
import type { ProjectRecord, SaveStatus } from './types';
import { cloneRecord } from './records';

export const DEFAULT_AUTOSAVE_DEBOUNCE_MS = 800;

export type SaveStatusListener = (status: SaveStatus, error?: Error) => void;

export interface AutosaveController {
  /** Queue a write. Repeated calls within the debounce window collapse into one. */
  schedule(record: ProjectRecord): void;
  /**
   * Write everything queued right now. Resolves once the store has settled;
   * never rejects — failures arrive through the status listeners instead, so a
   * page-unload handler cannot produce an unhandled rejection.
   */
  flush(): Promise<void>;
  readonly status: SaveStatus;
  /** Returns an unsubscribe function. Listeners are not called on subscribe. */
  subscribe(listener: SaveStatusListener): () => void;
  /**
   * Stops the timer and drops anything still queued. Call `flush` first if the
   * pending edits matter. Safe to call more than once.
   */
  dispose(): void;
}

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

export function createAutosave(
  store: ProjectStore,
  debounceMs: number = DEFAULT_AUTOSAVE_DEBOUNCE_MS,
): AutosaveController {
  const pending = new Map<string, ProjectRecord>();
  const listeners = new Set<SaveStatusListener>();

  let timer: ReturnType<typeof setTimeout> | null = null;
  /** Serialises writes: each drain chains onto the previous one. */
  let queue: Promise<void> = Promise.resolve();
  let status: SaveStatus = 'idle';
  let disposed = false;

  function emit(next: SaveStatus, error?: Error): void {
    status = next;
    // Copy first: a listener may unsubscribe while being notified.
    for (const listener of [...listeners]) {
      try {
        listener(next, error);
      } catch {
        // A broken subscriber must not take the save loop down with it.
      }
    }
  }

  function cancelTimer(): void {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
  }

  async function writePending(): Promise<void> {
    if (pending.size === 0) return;
    const batch = [...pending.values()];
    pending.clear();
    emit('saving');
    try {
      for (const record of batch) await store.put(record);
      emit('saved');
    } catch (error) {
      // Requeue what failed so a later flush can retry, but never clobber an
      // edit that arrived while this write was in flight.
      for (const record of batch) {
        if (!pending.has(record.id)) pending.set(record.id, record);
      }
      emit('error', toError(error));
    }
  }

  function drain(): Promise<void> {
    queue = queue.then(writePending);
    return queue;
  }

  function schedule(record: ProjectRecord): void {
    if (disposed) return;
    pending.set(record.id, cloneRecord(record));
    cancelTimer();
    timer = setTimeout(() => {
      timer = null;
      void drain();
    }, debounceMs);
  }

  async function flush(): Promise<void> {
    cancelTimer();
    // Even when disposed, await the queue so callers can wait out a write that
    // was already in flight.
    await (disposed ? queue : drain());
  }

  function subscribe(listener: SaveStatusListener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    cancelTimer();
    pending.clear();
    listeners.clear();
  }

  return {
    schedule,
    flush,
    subscribe,
    dispose,
    get status(): SaveStatus {
      return status;
    },
  };
}
